import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { ForgotPasswordDialog } from '@/components/ForgotPasswordDialog';
import { UnifiedLogin } from '@/components/auth/UnifiedLogin';
import { toast } from 'sonner';
import { ShieldCheck, ArrowLeft, UserCircle2, Sparkles } from 'lucide-react';
import { STAFF_ROLES } from '@/lib/roles';

export default function AdminAuth() {
    const nav = useNavigate();
    const [forgotOpen, setForgotOpen] = useState(false);

    /**
     * Called by UnifiedLogin after successful auth.
     * • Admins are auto-authenticated after step-1 (backend bypasses OTP for role==admin).
     * • Staff users go through the full 2FA flow (password + OTP).
     * Either way, we validate the role here before letting them into /admin.
     */
    const handleSuccess = (u) => {
        if (!u) return;
        if (!STAFF_ROLES.includes(u.role)) {
            toast.error('Not a staff / admin account — please use Customer login.');
            return;
        }
        nav('/admin');
        toast.success(`Welcome, ${u.name || u.email}`);
    };

    return (
        <div className='app-shell relative min-h-screen px-4 py-6 flex flex-col'>
            <div className='hero-gradient absolute inset-0 -z-10' />
            <button onClick={() => nav('/')} className='inline-flex items-center gap-1.5 text-sm text-muted-foreground mb-4 w-fit' data-testid='admin-auth-back'>
                <ArrowLeft className='w-4 h-4' /> Home
            </button>

            <div className='flex flex-col items-center text-center pt-2 pb-4 relative'>
                <div className='absolute top-16 w-72 h-24 -z-10 pointer-events-none'
                    style={{ background: 'radial-gradient(circle, rgba(30,58,138,0.30) 0%, transparent 70%)' }}
                />
                <img src='/uppower-logo.png' alt='UPPower Solar' className='h-14 w-auto mb-3 select-none' draggable='false' />
                <div className='inline-flex items-center gap-2 text-[15px] font-semibold rounded-full px-5 py-2 shadow-sm' style={{ background: 'rgba(30,58,138,0.10)', color: 'var(--brand-navy, #1E3A8A)' }}>
                    <ShieldCheck className='w-5 h-5' />
                    Staff Portal • स्टाफ पोर्टल
                </div>
                <p className='mt-3 text-[12.5px] text-muted-foreground max-w-[300px] leading-relaxed'>
                    Leads, quotations, applications, attendance और team का पूरा control एक ही जगह।
                </p>
            </div>

            <div className='w-full max-w-md mx-auto'>
                <Card className='card-elevate border-0 shadow-lg'>
                    <CardHeader className='pb-2'>
                        <CardTitle className='font-display text-center text-[16px]'>Login to your Staff account</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <UnifiedLogin
                            variant='staff'
                            onSuccess={handleSuccess}
                        />
                        <div className='mt-4 flex items-center justify-between text-xs'>
                            <a href='#' onClick={(e) => { e.preventDefault(); setForgotOpen(true); }} className='text-primary hover:underline' data-testid='admin-forgot-password'>
                                Forgot password?
                            </a>
                            <a href='#' onClick={(e) => { e.preventDefault(); toast.info('Please contact your administrator for account details.'); }} className='text-muted-foreground hover:underline' data-testid='admin-forgot-account'>
                                Forgot account?
                            </a>
                        </div>
                    </CardContent>
                </Card>

                <button onClick={() => nav('/customer/login')} className='mt-4 w-full inline-flex items-center justify-center gap-1.5 text-[12px] text-muted-foreground'>
                    <UserCircle2 className='w-3.5 h-3.5' /> क्या आप Customer हैं? <span className='underline'>यहाँ Login करें</span>
                </button>
            </div>

            <p className='mt-auto pt-6 text-center text-[10.5px] text-muted-foreground'>
                <Sparkles className='w-3 h-3 inline text-accent mr-0.5' /> Powered by UPPower Solar • PM Surya Ghar Yojana
            </p>
            <p className='pb-2 text-center text-[10.5px] text-muted-foreground'>
                Made with <span className='text-red-500'>♥</span> in India
            </p>

            <ForgotPasswordDialog open={forgotOpen} onClose={() => setForgotOpen(false)} />
        </div>
    );
}
